import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, MessageSquareWarning, LogOut, Plus, Menu, CalendarDays, ScrollText, UtensilsCrossed, Inbox, BookOpen, Users, Boxes, ClipboardList, History, Receipt } from "lucide-react";
import { useState } from "react";
import Logo from "@/components/Logo";
import { clearToken } from "@/lib/api";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox, testid: "nav-inquiries" },
  { to: "/admin/quotes", label: "Quotes", icon: ScrollText, testid: "nav-quotes" },
  { to: "/admin/contracts", label: "Contracts", icon: FileText, testid: "nav-contracts" },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/admin/public-menu", label: "Public Menu", icon: BookOpen, testid: "nav-public-menu" },
  { to: "/admin/menu", label: "Internal Menu", icon: UtensilsCrossed, testid: "nav-menu" },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory" },
  { to: "/admin/checkouts", label: "Check-outs", icon: ClipboardList, testid: "nav-checkouts" },
  { to: "/admin/employees", label: "Employees", icon: Users, testid: "nav-employees" },
  { to: "/admin/activity", label: "Activity Log", icon: History, testid: "nav-activity" },
  { to: "/admin/change-requests", label: "Change Requests", icon: MessageSquareWarning, testid: "nav-change-requests" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grain">
      {/* Top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#050505]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2 -ml-2 text-white/80 hover:text-[#C9963F] transition-colors"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              <Menu size={20} />
            </button>
            <Logo size="sm" />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/contracts/new")}
              className="hidden sm:inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D] transition-colors"
              data-testid="new-contract-top-btn"
            >
              <Plus size={16} /> New Contract
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 border border-white/15 text-white/80 px-3 py-2 text-sm hover:border-[#C9963F] hover:text-[#C9963F] transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 flex gap-8">
        {/* Sidebar (desktop) */}
        <aside className="hidden md:block w-56 shrink-0">
          <nav className="space-y-1 sticky top-24" data-testid="sidebar-nav">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors border-l-2 ${
                    isActive
                      ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/5"
                      : "border-transparent text-white/70 hover:text-white hover:border-white/30"
                  }`
                }
              >
                <it.icon size={16} strokeWidth={1.5} />
                {it.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="md:hidden fixed inset-0 z-40 bg-black/80 backdrop-blur" onClick={() => setOpen(false)}>
            <div className="absolute left-0 top-16 w-64 bg-[#0a0a0a] border-r border-white/10 h-full p-4" onClick={(e) => e.stopPropagation()}>
              <nav className="space-y-1">
                {navItems.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    onClick={() => setOpen(false)}
                    data-testid={`m-${it.testid}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 text-sm border-l-2 ${
                        isActive ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/5" : "border-transparent text-white/80"
                      }`
                    }
                  >
                    <it.icon size={16} strokeWidth={1.5} />
                    {it.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
